import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button } from 'components/button';
import MessageText from 'components/message/MessageText';
import Message from 'components/message/Message';
import { Card, CardBody } from 'components/card';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import messages from 'messages';
import {
  ROUTE_EXERCISES_TRAININGS_ENTITY,
  ROUTE_EXERCISES_TRAININGS_ENTITY_TRAINING_EXERCISES_EXERCISE
} from 'routes/RouteList';
import Icon from 'components/icon/Icon';
import { useStore } from 'stores/Store';
import TemplateForm from 'containers/pages/exercises/templates/TemplateForm';
import TemplateExercisesListSortable from 'containers/pages/exercises/templates/TemplateExercisesListSortable';
import { sortByArray } from 'utils/sort';
import { TemplateType } from 'constants';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import Template from 'models/Template';
import { useClickAway } from '@uidotdev/usehooks';
import { MODAL_TYPES } from 'models/ModalData';
import { StoreContext } from 'index';
import { useContextMenu } from 'contexts/ContextMenuProvider';
import useTrainings from 'hooks/queries/useTrainings';
import { useNotificationQueue } from 'components/notification';
import TrainingForm from 'containers/pages/exercises/trainings/TrainingForm';
import AddExercisesModal from 'containers/pages/exercises/exercises/AddExercisesModal';
import ShowEmbedUrlModal from 'containers/pages/exercises/trainings/ShowEmbedUrlModal';
import AddButton from 'containers/partials/buttons/AddButton';
import DownloadExerciseProgramTemplateButton from 'containers/partials/buttons/DownloadExerciseProgramTemplateButton';
import RemoveExercisesFromTemplateButton from 'containers/partials/buttons/RemoveExercisesFromTemplateButton';
import useTraining from 'hooks/queries/useTraining';
import RemoveExerciseTemplateButton from 'containers/partials/buttons/RemoveExerciseTemplateButton';

const TrainingExercisesTab = ({ onAddExercises }) => {
  const { uiState } = useContext(StoreContext);
  const intl = useIntl();
  const { entityId, templateId } = useParams();
  const { push } = useHistory();
  const ability = useAbility(AbilityContext);
  const notification = useNotificationQueue();

  const { addActionButton, setMenuItems, clearAllContext } = useContextMenu();

  const templateFormRef = useRef();
  const defaultSort = useRef([]);

  const training = useStore(state => state.training);
  const setTraining = useStore(state => state.setTraining);

  const setSelectedExercises = useStore(state => state.setSelectedExercises);
  const selectedExercises = useStore(state => state.selectedExercises);
  const selectExercises = useStore(state => state.selectExercises);
  const resetSelectedExercises = useStore(
    state => state.resetSelectedExercises
  );
  const templateSortableActive = useStore(
    state => state.templateSortableActive
  );
  const setTemplateSortableActive = useStore(
    state => state.setTemplateSortableActive
  );
  const { fetchTraining } = useTraining();
  const { fetchTrainings } = useTrainings(entityId);

  const [addExercisesModal, setAddExercisesModal] = useState(null);
  const [showEmbedUrl, setShowEmbedUrl] = useState(false);

  const sortableContainerRef = useClickAway(() => {
    if (templateSortableActive) {
      uiState.showModal({
        title: intl.formatMessage(
          messages.exercisesSortExercisesModalWarningTitle
        ),
        message: intl.formatMessage(
          messages.exercisesSortExercisesModalWarningMessage
        ),
        okLabel: intl.formatMessage(messages.saveButton),
        dismissLabel: intl.formatMessage(messages.buttonBack),
        type: MODAL_TYPES.WARNING,
        okHandler: () => {
          templateFormRef.current.submitForm();
        }
      });
    }
  });

  useEffect(() => {
    const contextButtons = [];

    if (ability.can('update', training)) {
      contextButtons.push(<AddExercisesMenuBtn />);
    }
    contextButtons.push(<DownloadExerciseProgramTemplateBtn />);
    if (
      ability.can('embed', 'Trainings') ||
      ability.can('share', 'Trainings')
    ) {
      contextButtons.push(<ShowEmbedUrlBtn />);
    }
    if (ability.can('delete', training)) {
      contextButtons.push(<RemoveExercisesFromTemplateBtn />);
    }
    if (ability.can('update', training)) {
      contextButtons.push(<SortExerciseBtn />);
    }
    if (ability.can('delete', training)) {
      contextButtons.push(<RemoveExerciseTemplateBtn />);
    }

    setMenuItems(contextButtons);

    if (ability.can('update', training)) {
      addActionButton(<AddExercisesBtn />);
    }

    return () => {
      clearAllContext();
    };
  }, [training, selectedExercises, templateSortableActive]);

  useEffect(() => {
    defaultSort.current = training.exercises.map(exercise => exercise.id);
    setSelectedExercises([]);
    return () => {
      resetSelectedExercises();
    };
  }, [training]);

  const onClickHandler = exerciseId => {
    push(
      generatePath(
        ROUTE_EXERCISES_TRAININGS_ENTITY_TRAINING_EXERCISES_EXERCISE,
        {
          entityId,
          templateId,
          exerciseId
        }
      )
    );
  };

  const onCancel = () => {
    setTemplateSortableActive(false);
    setTraining(
      new Template({
        ...training,
        exercises: sortByArray(training.exercises, defaultSort.current)
      })
    );
  };

  const onAddedHandler = () => {
    setAddExercisesModal(false);
  };

  const onTrainingDeleted = async templateId => {
    notification.add(`removed_${templateId}`, {
      message: intl.formatMessage(messages.messageRemovedtrainingsSuccess)
    });
    await fetchTrainings({
      variables: { entityId, type: TemplateType.TRAINING },
      fetchPolicy: 'network-only'
    });
    push(
      generatePath(ROUTE_EXERCISES_TRAININGS_ENTITY, {
        entityId
      })
    );
  };

  const AddExercisesBtn = () => (
    <AddButton
      expandable
      label={<FormattedMessage {...messages.exercisesAddExercisesButton} />}
      onClick={() => setAddExercisesModal(training)}
    />
  );

  const AddExercisesMenuBtn = () => (
    <Button menu onClick={() => setAddExercisesModal(training)}>
      <Icon id="add" />
      <FormattedMessage {...messages.exercisesAddExercisesButton} />
    </Button>
  );

  const DownloadExerciseProgramTemplateBtn = () => (
    <DownloadExerciseProgramTemplateButton
      templateId={training.id}
      collectionType={TemplateType.TRAINING}
    />
  );

  const ShowEmbedUrlBtn = () => (
    <Button menu onClick={() => setShowEmbedUrl(true)}>
      <Icon id="download" />
      <FormattedMessage {...messages.openCopyEmbedModal} />
    </Button>
  );

  const RemoveExercisesFromTemplateBtn = () => (
    <RemoveExercisesFromTemplateButton
      disabledMessage={
        templateSortableActive
          ? intl.formatMessage(
              messages.collectionRemoveExerciseDisabledDragging
            )
          : intl.formatMessage(
              messages.collectionRemoveExerciseDisabledNotSelected
            )
      }
      entityId={entityId}
      type="training"
      exerciseIds={selectedExercises.map(e => e.id)}
      templateId={templateId}
      updated={async () => {
        setSelectedExercises([]);
        await fetchTraining({
          variables: { templateId },
          fetchPolicy: 'network-only'
        });
        await fetchTrainings({
          variables: { entityId, type: TemplateType.TRAINING }
        });
      }}
      disabled={selectedExercises.length === 0}
    />
  );

  const SortExerciseBtn = () => (
    <Button
      menu
      onClick={() => setTemplateSortableActive(true)}
      disabled={selectedExercises.length > 0 || templateSortableActive}
    >
      {templateSortableActive ? (
        <FormattedMessage
          {...messages.collectionSortExercisesDisabledDragging}
        />
      ) : (
        <FormattedMessage {...messages.sortCollectionButton} />
      )}
      <Icon id="arrange" strokeColor="color-neutral-dark" />
    </Button>
  );

  const RemoveExerciseTemplateBtn = () => (
    <RemoveExerciseTemplateButton
      entityId={entityId}
      templateId={templateId}
      collectionType={TemplateType.TRAINING}
      menu
      onDeleted={templateId => onTrainingDeleted(templateId)}
    >
      <FormattedMessage {...messages.removeTrainingButton} />
    </RemoveExerciseTemplateButton>
  );

  return (
    <>
      <div
        ref={sortableContainerRef}
        className="o-flex o-flex--column"
        style={{
          flex: 1,
          marginBottom: templateSortableActive ? 72 : 0,
          overflowY: 'auto',
          overflowX: 'visible'
        }}
      >
        {training.exercises.length > 0 ? (
          <TemplateForm
            ref={templateFormRef}
            entityId={entityId}
            template={training}
            type={TemplateType.TRAINING}
            onComplete={() => setTemplateSortableActive(false)}
          >
            <TemplateExercisesListSortable
              exercises={training.exercises}
              checkable={!templateSortableActive}
              showNote={false}
              edit={!templateSortableActive}
              onClick={onClickHandler}
              onCancel={onCancel}
              selectedExercises={selectedExercises}
              onSelectedExercisesChange={selectExercises}
            />
          </TemplateForm>
        ) : (
          <Card centered>
            <CardBody empty>
              {ability.can('update', training) ? (
                <Message emptyState={true} icon="browse">
                  {training.exercises.length ? (
                    <MessageText>
                      <FormattedMessage
                        {...messages.exercisesListEmptyResult}
                      />
                    </MessageText>
                  ) : (
                    <MessageText>
                      <FormattedMessage {...messages.templateEmptyExercises} />
                    </MessageText>
                  )}
                  <Button
                    primary
                    rounded
                    onClick={() => onAddExercises(training)}
                  >
                    <Icon id="add-white" />
                    <FormattedMessage
                      {...messages.exercisesAddExercisesButton}
                    />
                  </Button>
                </Message>
              ) : (
                <Message emptyState={true} icon="browse">
                  {training.exercises.length ? (
                    <MessageText>
                      <FormattedMessage
                        {...messages.exercisesListEmptyResult}
                      />
                    </MessageText>
                  ) : (
                    <MessageText>
                      <FormattedMessage
                        {...messages.templateEmptyExercisesAndCantAdd}
                      />
                    </MessageText>
                  )}
                </Message>
              )}
            </CardBody>
          </Card>
        )}
      </div>
      {ability.can('update', training) && addExercisesModal && (
        <TrainingForm
          entityId={entityId}
          training={training}
          type={TemplateType.TRAINING}
          onComplete={() => setAddExercisesModal(false)}
        >
          {() => (
            <AddExercisesModal
              entityId={entityId}
              template={training}
              onClose={() => {
                setAddExercisesModal(false);
              }}
              onAdded={() => onAddedHandler()}
            />
          )}
        </TrainingForm>
      )}
      {showEmbedUrl &&
        (ability.can('embed', 'Trainings') ||
          ability.can('share', 'Trainings')) && (
          <ShowEmbedUrlModal
            isOpen={showEmbedUrl}
            templateId={templateId}
            onClose={() => setShowEmbedUrl(false)}
          />
        )}
    </>
  );
};

export default TrainingExercisesTab;
